import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { SnackbarProvider } from 'notistack';
import { store } from './store';
import { socketService } from './services/socket';
import { initializeWebSocketEvents, cleanupWebSocketEvents } from './services/websocket-events';
import { LayoutProvider } from './contexts/LayoutContext';
import { AppThemeProvider } from './contexts/AppThemeProvider';
import { AzureAuthProvider, useAzureAuth } from './contexts/AzureAuthContext';
import AuthLoading from './components/AuthLoading';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Chat from './pages/Chat';
import ChatNew from './pages/ChatNew';
import SecureMessages from './pages/SecureMessages';
import SecureVerification from './pages/SecureVerification';
import Tickets from './pages/Tickets';
import Meetings from './pages/Meetings';
import Notifications from './pages/Notifications';
import PushNotifications from './pages/PushNotifications';
import Settings from './pages/Settings';
import OpenHandler from './pages/OpenHandler';
import Clients from './pages/Clients';
import Users from './pages/Users';
import PrivateRoute from './components/PrivateRoute';
import LayoutWrapper from './components/LayoutWrapper';
import InstallPrompt from './components/InstallPrompt';
import './App.css';

// Auth wrapper component  
function AuthWrapper({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated, error, login, autoLogin, account } = useAzureAuth();
  const [loginAttempted, setLoginAttempted] = React.useState(false);

  React.useEffect(() => {
    // Try auto-login once when component mounts
    if (!isLoading && !isAuthenticated && !error && !loginAttempted) {
      console.log('Attempting auto-login...');
      setLoginAttempted(true);
      // Try the auto-login which uses popup method
      autoLogin();
    }
  }, [isLoading, isAuthenticated, error, loginAttempted, autoLogin]);

  // Debug logging
  React.useEffect(() => {
    console.log('AuthWrapper state:', { 
      isLoading, 
      isAuthenticated, 
      error, 
      hasAccount: !!account,
      loginAttempted 
    });
  }, [isLoading, isAuthenticated, error, account, loginAttempted]);

  // Show loading screen while checking auth
  if (isLoading) {
    return <AuthLoading message="Authenticating with Microsoft 365..." />;
  }

  // Show error if auth failed
  if (error) {
    return <AuthLoading message={`Authentication failed: ${error}`} />;
  }

  // If not authenticated and haven't attempted login yet, show redirecting
  if (!isAuthenticated && !loginAttempted) {
    return <AuthLoading message="Initializing authentication..." />;
  }

  // If not authenticated but login was attempted, show manual login button
  if (!isAuthenticated && loginAttempted) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" gutterBottom color="primary">
            IT Control Panel
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please sign in with your Microsoft account to continue
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={login}
            sx={{ 
              backgroundColor: '#2b3e7d',
              '&:hover': {
                backgroundColor: '#1e2b5a'
              }
            }}
          >
            Sign in with Microsoft
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
}

// Component to handle BroadcastChannel inside Router
function AppWithBroadcast() {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Request notification permission if not granted
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Set up BroadcastChannel for PWA communication
    const channel = new BroadcastChannel('itcp-pwa');
    
    channel.onmessage = (event) => {
      if (event.data.type === 'ping') {
        // Respond that PWA is active
        channel.postMessage({ type: 'pwa-active' });
      } else if (event.data.type === 'navigate') {
        console.log('Navigate request received:', event.data.url);
        
        // Navigate to requested URL
        navigate(event.data.url);
        
        // Try to focus the window
        window.focus();
        
        // Flash the title first (this should always work)
        const originalTitle = document.title;
        let flashCount = 0;
        const flashInterval = setInterval(() => {
          document.title = flashCount % 2 === 0 ? '● IT Control Panel' : originalTitle;
          flashCount++;
          if (flashCount > 6) {
            clearInterval(flashInterval);
            document.title = originalTitle;
          }
        }, 500);
        
        // Show notification to help user switch to the app
        console.log('Notification permission:', Notification.permission);
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const notification = new Notification('IT Control Panel', {
              body: 'App is already open - click here to switch',
              icon: '/logo192.png',
              badge: '/logo192.png',
              tag: 'focus-request-' + Date.now(), // Unique tag to always show
              requireInteraction: true, // Don't auto-dismiss
              silent: false // Allow sound
            });
            
            console.log('Notification created:', notification);
            
            notification.onclick = () => {
              console.log('Notification clicked');
              window.focus();
              notification.close();
            };
            
            // Auto-close after 4 seconds if not clicked
            setTimeout(() => notification.close(), 4000);
          } catch (err) {
            console.error('Notification failed:', err);
            // Try using service worker notification as fallback
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
              navigator.serviceWorker.ready.then(registration => {
                registration.showNotification('IT Control Panel', {
                  body: 'App is already open - click here to switch',
                  icon: '/logo192.png',
                  badge: '/logo192.png',
                  tag: 'focus-request-' + Date.now(),
                  requireInteraction: true,
                  silent: false
                });
              });
            }
          }
        } else {
          console.log('Notifications not available or not granted');
        }
      }
    };
    
    return () => {
      channel.close();
    };
  }, [navigate]);
  
  return (
    <AuthWrapper>
      <Routes>
        <Route path="/open" element={<OpenHandler />} />
        <Route element={<LayoutWrapper />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/users" element={<Users />} />
          <Route path="/chat" element={<ChatNew />} />
          <Route path="/chat-old" element={<Chat />} />
          <Route path="/secure-messages" element={<SecureMessages />} />
          <Route path="/secure-verification" element={<SecureVerification />} />
          <Route path="/tickets/*" element={<Tickets />} />
          <Route path="/meetings" element={<Meetings />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/push-notifications" element={<PushNotifications />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* Keep login route for fallback/troubleshooting */}
          <Route path="/login" element={<Login />} />
        </Route>
      </Routes>
    </AuthWrapper>
  );
}

function App() {
  useEffect(() => {
    // Don't connect immediately - wait for login
    // The socket connection will be established after successful login
    
    return () => {
      cleanupWebSocketEvents();
      socketService.disconnect();
    };
  }, []);

  return (
    <Provider store={store}>
      <LayoutProvider>
        <AppThemeProvider>
          <CssBaseline />
          <SnackbarProvider maxSnack={3}>
            <AzureAuthProvider>
              <InstallPrompt />
              <Router>
                <AppWithBroadcast />
              </Router>
            </AzureAuthProvider>
          </SnackbarProvider>
        </AppThemeProvider>
      </LayoutProvider>
    </Provider>
  );
}

export default App;
